import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { PageScrollService } from 'ngx-page-scroll-core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { ConfigureService } from '@app/configure/services/configure.service';
import { ModalService } from '@app/core/services/modal.service';
import { SessionService } from '@app/core/services/session.service';
import { HelperService } from '@app/core/services/helper.service';

import { Dealer } from '@app/core/models/dealer.model';
import { environment } from '@environments/environment';

@Component({
	selector: 'cng-configure-payment',
	templateUrl: './payment.component.html',
	styleUrls: ['./payment.component.less'],
})
export class ConfigurePaymentComponent implements OnInit, OnDestroy {
	current_step_key: string = 'payment';
	step_mode: string = this.configure_service.getStepMode();
	dealer: Dealer = this.session_service.getDealer();
	enquiry_id: string = this.configure_service.configure_data.meta.enquiry_id;
	vehicle: any = this.configure_service.configure_data.steps.purchase_vehicle.data;
	payment: any = this.helper_service.copy(this.configure_service.configure_data.steps.payment.data);
	grand_total: number = this.configure_service.configure_data.steps.customise_review.data.grand_total;
	max_cashback_reached: boolean = this.isMaxCashbackReached(this.configure_service.configure_data);
	expanded_provider: string = null;
	page_scroll_offset: number = 130;
	buy_now_enabled: boolean = this.configure_service.isBuyNowEnabled();
	payment_type: 'buyNow' | 'reserve' = this.buy_now_enabled ? 'buyNow' : 'reserve';
	show_summary: boolean = false;

	destroyed$: Subject<boolean> = new Subject<boolean>();
	concierge_image: any = {
		'background-image': `url(${environment.deploy_protocol}${environment.deploy_domain}/assets/img/concierge.png)`,
	};

	visa_logo: string = `${environment.deploy_protocol}${environment.deploy_domain}/assets/img/visa.png`;
	mastercard_logo: string = `${environment.deploy_protocol}${environment.deploy_domain}/assets/img/mastercard.png`;
	paypal_logo: string = `${environment.deploy_protocol}${environment.deploy_domain}/assets/img/paypal.png`;
	stripe_logo: string = `${environment.deploy_protocol}${environment.deploy_domain}/assets/img/stripe.png`;
	payment_sense_logo: string = `${environment.deploy_protocol}${environment.deploy_domain}/assets/img/payment-sense.png`;

	constructor(
		private configure_service: ConfigureService,
		private session_service: SessionService,
		private page_scroll_service: PageScrollService,
		private modal_service: ModalService,
		private helper_service: HelperService
	) {
		if (!this.configure_service.isStepEnabled('payment')) {
			this.configure_service.navigateToStep('purchase_vehicle');
		}
	}

	ngOnInit(): void {
		this.checkWidth();

		if (this.payment.payment_providers) {
			this.payment.payment_providers.push({
				reference: 'telephone',
			});
		}

		this.configure_service.configure_data$.pipe(takeUntil(this.destroyed$)).subscribe((configure_data) => {
			this.enquiry_id = configure_data.meta.enquiry_id;
			this.vehicle = configure_data.steps.purchase_vehicle.data;
			this.payment = this.helper_service.copy(configure_data.steps.payment.data);
			this.max_cashback_reached = this.isMaxCashbackReached(configure_data);
			this.helper_service.scrollTop();

			if (this.payment.payment_providers) {
				this.payment.payment_providers.push({
					reference: 'telephone',
				});

				const expanded_provider = this.payment.payment_providers.find((provider) => {
					return provider.reference === this.expanded_provider;
				});
				if (expanded_provider) {
					this.onAccordionChange(expanded_provider);
				}
			}
		});

		this.configure_service.track('step.moved', { step: this.current_step_key }).subscribe();
		this.configure_service.trackJourney().subscribe();

		if (!!this.dealer.concierge_image) {
			this.concierge_image['background-image'] = `url(${this.dealer.concierge_image})`;
		}
	}

	onTogglePaymentType(type: 'buyNow' | 'reserve'): void {
		this.payment_type = type;
	}

	onAccordionChange(provider: any): void {
		for (const p of this.payment.payment_providers) {
			p.selected = p === provider ? !provider.selected : false;
		}

		this.expanded_provider = provider.selected ? provider.reference : null;

		setTimeout(() => {
			this.page_scroll_service.scroll({
				document: document,
				scrollTarget: `#${provider.reference}`,
				scrollOffset: this.page_scroll_offset,
				duration: 50,
			});
		}, 200);
	}

	isMaxCashbackReached(configure_data): boolean {
		if (configure_data.steps.customise_review.data.grand_total >= 0) {
			return false;
		}

		return (
			configure_data.steps.customise_review.data.grand_total <
			-this.configure_service.configure_data.meta.max_cashback
		);
	}

	showCallback(): void {
		this.modal_service.show(['configure/callback']);
	}

	@HostListener('window:resize')
	checkWidth(): void {
		if (this.helper_service.isScreenWidth('<=', 'xxs')) {
			this.page_scroll_offset = 135;
		} else if (this.helper_service.isScreenWidth('<=', 'xs')) {
			this.page_scroll_offset = 165;
		} else if (this.helper_service.isScreenWidth('<=', 'md')) {
			this.page_scroll_offset = 195;
		} else {
			this.page_scroll_offset = 215;
		}
	}

	jumpTo(new_step_key: string): void {
		this.saveAndGoTo(new_step_key);
	}

	saveAndGoTo(new_step_key: string): void {
		this.configure_service.navigateToStep(new_step_key);
	}

	onShowContact(): void {
		this.modal_service.show(['dealer/contact'], {
			callback_enabled: false,
		});
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
	}
}
